/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { When } from "react-if";
import { Text } from "@nordcloud/gnui";
import { CloudAccountCloudProvider } from "~/generated/graphql";
import { Mode } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import { isNotEmpty, isNotNil } from "~/tools";
import { State } from "./types";

type Props = {
  state: State;
  updateState: Dispatch<Partial<State>>;
  provider?: CloudAccountCloudProvider;
  planCheckboxes: Item[];
  cloudAccountCheckboxes: Item[];
  resourceGroupCheckboxes: Item[];
  plansLoading?: boolean;
  cloudAccountsLoading?: boolean;
  resourceGroupsLoading?: boolean;
};

export function ResourceOriginFilters({
  state,
  updateState,
  provider,
  planCheckboxes,
  cloudAccountCheckboxes,
  resourceGroupCheckboxes,
  plansLoading = false,
  cloudAccountsLoading = false,
  resourceGroupsLoading = false,
}: Props) {
  const clearCloudAccountsFilter = () => {
    updateState({ cloudAccounts: undefined, excludeCloudAccounts: undefined });
  };

  const clearResourceGroupsFilter = () => {
    updateState({
      resourceGroups: undefined,
      excludeResourceGroups: undefined,
    });
  };

  const clearPlansFilter = () => {
    updateState({
      plans: undefined,
      excludePlans: undefined,
    });
  };

  return (
    <>
      <Text size="md" weight="medium">
        Resource Origin
      </Text>
      <When condition={!provider}>
        <FilterWrapper
          name="Cloud Account"
          isSelected={
            isNotNil(state.cloudAccounts) && isNotEmpty(state.cloudAccounts)
          }
          selectedOptions={state.cloudAccounts ?? []}
          onClear={clearCloudAccountsFilter}
        >
          <SelectableListWithSearch
            isLoading={cloudAccountsLoading}
            allowModeChange
            selectedOptions={state.cloudAccounts ?? []}
            selectableItems={cloudAccountCheckboxes}
            isExcludeSelected={state.excludeCloudAccounts}
            onMultiSelect={(selectedCloudAccounts) =>
              updateState({ cloudAccounts: selectedCloudAccounts })
            }
            onModeChange={(mode) =>
              updateState({ excludeCloudAccounts: mode === Mode.EXCLUDES })
            }
          />
        </FilterWrapper>
      </When>
      <FilterWrapper
        name="Resource Group"
        isSelected={
          isNotNil(state.resourceGroups) && isNotEmpty(state.resourceGroups)
        }
        selectedOptions={state.resourceGroups ?? []}
        onClear={clearResourceGroupsFilter}
      >
        <SelectableListWithSearch
          isLoading={resourceGroupsLoading}
          allowModeChange
          selectedOptions={state.resourceGroups ?? []}
          selectableItems={resourceGroupCheckboxes}
          isExcludeSelected={state.excludeResourceGroups}
          onMultiSelect={(selectedResourceGroups) =>
            updateState({ resourceGroups: selectedResourceGroups })
          }
          onModeChange={(mode) =>
            updateState({ excludeResourceGroups: mode === Mode.EXCLUDES })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Plan"
        isSelected={isNotNil(state.plans) && isNotEmpty(state.plans)}
        selectedOptions={state.plans ?? []}
        onClear={clearPlansFilter}
      >
        <SelectableListWithSearch
          isLoading={plansLoading}
          allowModeChange
          selectedOptions={state.plans ?? []}
          selectableItems={planCheckboxes}
          isExcludeSelected={state.excludePlans}
          onMultiSelect={(selectedPlans) =>
            updateState({ plans: selectedPlans })
          }
          onModeChange={(mode) =>
            updateState({ excludePlans: mode === Mode.EXCLUDES })
          }
        />
      </FilterWrapper>
    </>
  );
}
